import React, { useRef, useEffect, useState, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import { TextAreaContainer } from './style'
import { getStringLength } from 'components/utils/stringCheck'
import { InputCharacterCounter, Size, ReadOnly } from '..'
import IconButton from 'particles/Button/IconButton'
import Tooltip from 'particles/Tooltip'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import Text from 'particles/Text'
import Icon, { IconName, IconSize } from 'particles/Icon'
import _ from 'lodash'
import CommentContainer from 'components/Shared/Comment'
import { useDebounce } from 'hooks'

export const sizeMap: Record<Size, { padding: number; lineHeight: number }> = {
    m: {
        padding: 22,
        lineHeight: 20,
    },
    s: {
        padding: 14,
        lineHeight: 20,
    },
}

interface TextAreaProps {
    className?: string
    label?: string
    subtext?: string
    size?: Size
    isRequired?: boolean
    showInputLength?: boolean
    maxInputLength?: number
    placeholder?: string
    width?: number | string
    isDisabled?: boolean
    tooltipContext?: { title?: string; label?: string }
    minTextAreaHeight?: number
    maxTextAreaHeight?: number
    error?: string
    isReadOnly?: boolean
    onChange: (value: string) => void
    isCommentable?: boolean
    isCommented?: boolean
    onLabelClick?: () => void
    onFocusCallBack?: (isFocus: boolean) => void
    autoFocus?: boolean
    value: string
    isCardItem?: boolean
    isDeletable?: boolean
    onDelete?: (e: React.MouseEvent<HTMLButtonElement>) => void
    inputIcon?: IconName
    inputIconSize?: IconSize
    identifier?: string
    dataTestId?: string
    labelSize?: LabelSize
    link?: LinkConfig
    labelTooltip?: string
    hideReadOnlyText?: boolean
    showError?: boolean
}

const TextArea: React.FC<TextAreaProps> = ({
    className,
    label = '',
    subtext,
    size = 'm',
    width,
    isDisabled = false,
    isRequired = false,
    showInputLength = false,
    placeholder,
    maxInputLength = 100,
    maxTextAreaHeight,
    tooltipContext = { title: '', label: '' },
    minTextAreaHeight,
    error,
    isReadOnly = false,
    isCommentable = false,
    isCommented = false,
    onChange,
    onLabelClick,
    onFocusCallBack,
    autoFocus = false,
    value,
    isCardItem = false,
    isDeletable = false,
    onDelete,
    inputIcon,
    inputIconSize = '20px',
    identifier,
    dataTestId,
    labelSize,
    link,
    labelTooltip,
    hideReadOnlyText,
    showError,
    ...props
}) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const textAreaContainerRef = useRef<HTMLDivElement>(null)
    const isDecreasing = useRef(false)

    const [isFocus, setIsFocus] = useState(false)

    const minimumTextAreaSize = useMemo(() => {
        const minimumInputAreaHeight = minTextAreaHeight ? minTextAreaHeight - sizeMap[size].padding : undefined
        return minimumInputAreaHeight
            ? minimumInputAreaHeight > sizeMap[size].lineHeight
                ? minimumInputAreaHeight
                : sizeMap[size].lineHeight
            : undefined
    }, [minTextAreaHeight, size])

    useEffect(() => {
        // textareaResize
        if (textAreaRef && textAreaRef.current) {
            if (!_.isEmpty(value) && textAreaRef.current.scrollHeight !== minimumTextAreaSize) {
                if (isDecreasing.current) {
                    textAreaRef.current.style.height = 'auto'
                }
                textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px'
                isDecreasing.current = false
            } else if (_.isEmpty(value)) {
                textAreaRef.current.style.height = minimumTextAreaSize + 'px'
            }
        }
    }, [value, minimumTextAreaSize, isFocus, isReadOnly, isDisabled])

    useEffect(() => {
        if (onFocusCallBack) {
            onFocusCallBack(isFocus)
        }
        if (isFocus && textAreaRef.current && textAreaContainerRef.current) {
            textAreaContainerRef.current.scrollTop = textAreaContainerRef.current.scrollHeight
            textAreaRef.current.selectionStart = textAreaRef.current.value.length
            textAreaRef.current.selectionEnd = textAreaRef.current.value.length
        }
    }, [isFocus, isReadOnly])

    useEffect(() => {
        if (autoFocus && textAreaRef.current && !isReadOnly) {
            textAreaRef.current.focus()
        }
    }, [autoFocus, isReadOnly])

    return (
        <TextAreaContainer
            width={width}
            minTextAreaHeight={minimumTextAreaSize}
            maxTextAreaHeight={maxTextAreaHeight}
            className={`${className} text-area-container`}
            hasError={showError ?? Boolean(error)}
            size={size}
            data-testid={dataTestId}
        >
            {(label || subtext) && (
                <CommentContainer identifier={identifier}>
                    <Label
                        title={label || ''}
                        subtext={subtext}
                        isRequired={isRequired}
                        size={labelSize ? labelSize : size}
                        isCommentable={isCommentable}
                        isCommented={isCommented}
                        onClick={onLabelClick}
                        link={link}
                        tooltip={labelTooltip}
                    />
                </CommentContainer>
            )}
            {isReadOnly ? (
                !hideReadOnlyText && (
                    <ReadOnly
                        width={width}
                        size={size}
                        value={value}
                        displayType={isCardItem ? 'card-item' : 'regular'}
                        minTextAreaHeight={minimumTextAreaSize}
                        className="readonly-text-area"
                    />
                )
            ) : (
                <div className={'input-count-container'}>
                    <div
                        className={`textarea-input-container ${isDisabled ? 'disabled' : ''} ${isFocus ? 'focus' : ''}`}
                        ref={textAreaContainerRef}
                    >
                        {inputIcon && (
                            <Icon
                                name={inputIcon}
                                size={inputIconSize}
                                color={isDisabled ? 'placeholder' : 'primary'}
                                className="input-icon icon"
                            />
                        )}
                        <textarea
                            {...props}
                            value={value}
                            required={isRequired}
                            placeholder={placeholder}
                            disabled={isDisabled}
                            ref={textAreaRef}
                            rows={1}
                            onFocus={() => {
                                setIsFocus(true)
                            }}
                            onBlur={() => {
                                setIsFocus(false)
                            }}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                if (e.target.value < value) {
                                    isDecreasing.current = true
                                }
                                onChange(e.target.value)
                            }}
                            data-emoji-input="unicode"
                        />
                        {!isReadOnly && !isDisabled && isDeletable ? (
                            <IconButton
                                icon="x"
                                form="icon"
                                onClick={onDelete}
                                size="xs"
                                color="neutral"
                                isDisabled={isDisabled}
                                className="icon"
                            />
                        ) : null}
                        {isDisabled && (tooltipContext?.title || tooltipContext?.label) && (
                            <Tooltip placement="bottom" title={tooltipContext?.title} label={tooltipContext?.label}>
                                <IconButton
                                    icon="question"
                                    size="xs"
                                    form="icon"
                                    color="neutral"
                                    className="tooltip-icon"
                                    onClick={e => {
                                        // Prevent disable button click trigger editor's validation event, disable button should only used for show tooltip
                                        e.preventDefault()
                                    }}
                                />
                            </Tooltip>
                        )}
                    </div>
                    {(error || showInputLength) && (
                        <div className="counter-and-error">
                            <Text scale="caption400" color="error" className="error" as="div">
                                {error}
                            </Text>
                            {showInputLength && (
                                <InputCharacterCounter
                                    inputCharLength={getStringLength(value)}
                                    maxInputLength={maxInputLength}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </TextAreaContainer>
    )
}
export default TextArea

export interface FormikTextAreaProps extends Omit<TextAreaProps, 'value' | 'onChange'> {
    value?: string
    name: string
    isError?: boolean
    onChange?: (value: string) => void
    validateByTouch?: boolean
    showError?: boolean
}

const FormikTextArea: React.FC<FormikTextAreaProps> = ({ onChange, validateByTouch, showError = true, ...props }) => {
    const [field, meta] = useField(props.name)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const [displayValue, setDisplayValue] = useState(field?.value)
    const onChangeDebounce = useDebounce(value => setFieldValue(field.name as never, value), 200)
    const hasError = (showError && (validateByTouch ? meta.touched && !!meta.error : !!meta.error)) || props.isError

    const handleChange = (value: string) => {
        setDisplayValue(value)
        if (onChange) {
            onChange(value)
            if (!meta.touched) setFieldTouched(field.name as never, true, false)
            return
        }
        onChangeDebounce(value)
    }
    useEffect(() => {
        setDisplayValue(field.value)
    }, [field.value])

    return <TextArea {...props} value={displayValue} error={(hasError && meta.error) || ''} onChange={handleChange} />
}
export { FormikTextArea }
